import React, { useEffect, useState } from "react"
import { Grid, Typography } from "@mui/material"
import FeedItem from "./FeedItem"
import { useApiService } from "../hooks/Api/useApiService"
import Spinner from "../UI/Spinner/Spinner"
import FilterStripe from "./FilterStripe"
import useDeviceType from "../hooks/Utils/useDeviceType"
import { useLocation } from "react-router-dom"
import T from "../hooks/Language/T"

const Feed = (props) => {
  const [skills, setSkills] = useState([])
  const [fetching, setFetching] = useState(false)
  const [page, setPage] = useState(1)
  const [moreSkills, setMoreSkills] = useState(true)
  const [moreGroups, setMoreGroups] = useState(true)
  const [moreRefs, setMoreRefs] = useState(true)
  const [should_fetch, setShouldFetch] = useState(false)
  const { getData, postData } = useApiService()
  const location = useLocation()
  const [selected_filter, setSelectedFilter] = useState(
    props.user_name ? { type: "userName", value: props.user_name } : null
  )
  const [finished_deleting, setFinishedDeleting] = useState(false)
  const deviceType = useDeviceType()
  const isDesktop = deviceType === "desktop"
  useEffect(() => {
    setFinishedDeleting(false)
    if (props.user_name) {
      setSelectedFilter({ type: "userName", value: props.user_name })
    } else {
      setSelectedFilter(null)
    }
  }, [props.user_name])

  useEffect(() => {
    const fetchData = async () => {
      if (fetching) return
      setFetching(true)

      // Reset page and flags on initial load
      if (props.scrollPercentage === 0 || finished_deleting || selected_filter) {
        setPage(1)
        setMoreSkills(true)
        setMoreGroups(true)
        setMoreRefs(true)
      }

      try {
        if (selected_filter) {
          await getFilteredSkills()
        } else if (props.scrollPercentage === 0) {
          if (props.groups === "true") {
            await loadInitialGroups()
          } else {
            await loadInitialSkillsAndGroups()
          }
          setPage(2)
        } else if (props.scrollPercentage >= 95) {
          if (props.groups === "true" && moreGroups) {
            await loadMoreGroups()
          } else if (!props.groups && (moreSkills || moreGroups || moreRefs)) {
            await loadMoreSkillsAndGroups()
          }
        }
      } finally {
        setFetching(false)
      }
    }

    fetchData()
  }, [props.scrollPercentage, selected_filter, finished_deleting, props.user_name, should_fetch])

  const getFilteredSkills = async () => {
    const data = await postData("skills/getfilteredskills", { selected_filter })
    const all = [...(data?.skills || []), ...(data?.groups || [])]

    setSkills(all)
  }

  const loadInitialSkillsAndGroups = async () => {
    const [skillsRes, groupsRes, refsRes] = await Promise.all([
      getData("skills/getlastfiveskills"),
      getData("groups/getlastfivegroups"),
      getData("skills/getlastfivereflections"),
    ])

    const all = [
      ...(skillsRes?.skills || []),
      ...(groupsRes?.groups || []),
      ...(refsRes?.reflections || []),
    ]

    setSkills(all)
    setMoreSkills(!!skillsRes?.skills)
    setMoreGroups(!!groupsRes?.groups)
    setMoreRefs(!!refsRes?.reflections)
  }

  const loadInitialGroups = async () => {
    const groupsRes = await getData("groups/getlastfivegroups")
    setSkills(groupsRes?.groups || [])
    setMoreGroups(!!groupsRes?.groups)
  }

  const loadMoreSkillsAndGroups = async () => {
    const [skillsRes, groupsRes, refsRes] = await Promise.all([
      postData("skills/getfiveskills", { page }),
      postData("groups/getfivegroups", { page }),
      postData("skills/getfivereflections", { page }),
    ])

    const newItems = [
      ...(skillsRes?.skills || []),
      ...(groupsRes?.groups || []),
      ...(refsRes?.reflections || []),
    ]
    setSkills((prev) => [...prev, ...newItems])

    if (!skillsRes?.skills) setMoreSkills(false)
    if (!groupsRes?.groups) setMoreGroups(false)
    if (!refsRes?.reflections) setMoreRefs(false)

    setPage((prev) => prev + 1)
  }

  const loadMoreGroups = async () => {
    const groupsRes = await postData("groups/getfivegroups", { page })
    if (groupsRes?.groups) {
      setSkills((prev) => [...prev, ...groupsRes.groups])
    } else {
      setMoreGroups(false)
    }
    setPage((prev) => prev + 1)
  }

  return (
    <div style={{ alignItems: "center", display: "flex", flexDirection: "column" }}>
      <Grid style={{ width: props.groups === "true" && isDesktop ? "50%" : "100%" }}>
        {!props.user_name && props.groups !== "true" && isDesktop && (
          <>
            <FilterStripe setSelectedFilter={setSelectedFilter} />
          </>
        )}
        {!fetching && (!skills || skills.length === 0) ? (
          <T>
            <div
              style={{
                height: "100vh",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography variant='h6' color='textSecondary' style={{ marginTop: "2rem" }}>
                {location.pathname === "/home"
                  ? "No skills or groups found."
                  : "Here you can find your skills and groups. go ahead and create your first one!"}
              </Typography>
            </div>
          </T>
        ) : (
          <Grid item xs={12} sm={8} md={6} marginTop={"1rem"}>
            {skills?.map((skill, index) => (
              <FeedItem
                key={index}
                skill={skill}
                finished_deleting={finished_deleting}
                should_fetch={should_fetch}
                setShouldFetch={setShouldFetch}
                setFinishedDeleting={setFinishedDeleting}
                image={props.image}
                is_group={skill.max_participants ? true : false}
              />
            ))}
          </Grid>
        )}
      </Grid>
      {fetching && (
        <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)" }}>
          <Spinner />
        </div>
      )}
    </div>
  )
}

export default Feed
